/* eslint-disable ember/use-ember-data-rfc-395-imports */
import { Configuration, StatusApi, type StatusApiListDevicesRequest } from '@envoy/devices_client';
import type DS from 'ember-data';
import type { ModelSchema } from 'ember-data';
import { type ModelRegistry } from 'ember-data/model';
import ApplicationAdapter from 'garaje/adapters/application';
import config from 'garaje/config/environment';
import { Promise as EmberPromise } from 'rsvp';

export default class DeviceStatusAdapter extends ApplicationAdapter {
  host = config.devicesHost;

  query<K extends keyof ModelRegistry>(
    _store: DS.Store,
    _type: ModelSchema<K>,
    query: Record<string, unknown>,
  ): EmberPromise<Record<string, unknown>> {
    const locationId = this.extractScopeId(query, 'location');
    const zoneId = this.extractScopeId(query, 'zone');
    const statusApi = this.buildStatusApi();

    if (!locationId && !zoneId) {
      throw new Error('device-status query requires a location_id or zone_id');
    }

    const statusApiRequest = this.buildStatusApiRequest({ query, locationId, zoneId });
    return new EmberPromise((resolve, reject) => {
      statusApi
        .listDevices(statusApiRequest)
        .then(({ data }) => {
          resolve(data as unknown as Record<string, unknown>);
        })
        .catch(reject);
    });
  }

  private extractScopeId(query: Record<string, unknown>, scope: 'location' | 'zone'): string | null {
    const snakeKey = scope === 'zone' ? 'zone_id' : 'location_id';
    const camelKey = scope === 'zone' ? 'zoneId' : 'locationId';
    const value = typeof query[snakeKey] === 'string' ? query[snakeKey] : query[camelKey];

    if (typeof value === 'string' && value.length > 0) {
      return value;
    }

    return null;
  }

  private buildStatusApi(): StatusApi {
    const statusApiConfiguration = new Configuration({
      basePath: this.host,
      baseOptions: {
        withCredentials: true,
      },
    });

    return new StatusApi(statusApiConfiguration, this.host);
  }

  private buildStatusApiRequest({
    query,
    locationId,
    zoneId,
  }: {
    query: Record<string, unknown>;
    locationId: string | null;
    zoneId: string | null;
  }): StatusApiListDevicesRequest {
    const rawLimit = query['limit'];
    const normalizedLimit = typeof rawLimit === 'string' ? Number(rawLimit) : rawLimit;

    return {
      ...(locationId ? { locationId } : {}),
      ...(zoneId ? { zoneId } : {}),
      ...(typeof query['product'] === 'string' ? { product: query['product'] } : {}),
      ...(typeof query['cursor'] === 'string' && query['cursor'] ? { cursor: query['cursor'] } : {}),
      ...(typeof normalizedLimit === 'number' && !Number.isNaN(normalizedLimit)
        ? { limit: normalizedLimit }
        : typeof rawLimit === 'number'
          ? { limit: rawLimit }
          : {}),
    } as StatusApiListDevicesRequest;
  }
}
